import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Grid,
  Typography,
  Alert,
  Divider,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import GoogleSignIn from "../components/GoogleSignIn";
//import { useToast } from "../context/ToastContext";
import { useUser } from "../context/UserContext";
import API from "../api/axiosConfig";

import SecurityIcon from "@mui/icons-material/Security";
import GroupsIcon from "@mui/icons-material/Groups";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); 

  const { user: contextUser, loading: authLoading } = useUser();
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state
  const [errorMessage, setErrorMessage] = useState(null); // Tracks OAuth/URL errors
  //const showToast = useToast();

  useEffect(() => {
    const alertParam = searchParams.get("alert");
    const errorParam = searchParams.get("error");
    const expiredParam = searchParams.get("expired");

    // Check URL parameters for generic alerts
    if (alertParam) {
      setErrorMessage(alertParam);
      navigate("/", { replace: true }); // clear the message from URL
      return;
    }

    // Check for error messages related to signing in
    if (errorParam) {
      setErrorMessage(
        "Google Sign-In failed or was cancelled. Please try again.",
      );
      navigate("/", { replace: true }); // Clean query parameters from URL
    }

    // Session expired handling
    if (expiredParam === "true") {
      setErrorMessage("Your session has expired. Please sign in again.");
      navigate("/", { replace: true });
      return;
    }
    
    // Fetch home page widget data using HttpOnly cookie session
    const fetchHomeData = async () => {
      try {
        // Browser automatically attaches HttpOnly accessToken cookie
        const response = await API.post("/api/home/getInfo");
        setHomeData(response.data);
      } catch (error) {
        console.error("Error fetching home dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (contextUser) {
      fetchHomeData();
    } else {
      setLoading(false);
    }
  }, [searchParams, contextUser, navigate]);

  if (authLoading || (contextUser && loading)) {
    // Show loading state while fetching user data
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <i>Loading welcome page...</i>
      </div>
    );
  }

  if (!contextUser) {
    // If no user token is found
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 3,
          textAlign: "center",
          width: "100%",
        }}
      >
        <Typography variant="h4">Welcome to Lionheart!</Typography>
        <Typography
          sx={(theme) => ({
            fontStyle: "italic",
            color: theme.palette.text.secondary,
          })}
        >
          S3K's Custom Employee Portal
        </Typography>
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mt: 3, maxWidth: "400px", width: "90%" }}
          >
            {errorMessage}
          </Alert>
        )}
        <Box
          sx={{
            width: "70vw",
            maxWidth: "300px", 
            mt: 4,
          }}
        >
          <GoogleSignIn />
        </Box>
      </Box>
    );
  }

  // Combined data from home endpoint and user context
  const user = { ...contextUser, ...homeData };

  // If logged in, show the welcome message
  return (
    <Box sx={{ p: 4, maxWidth: "1000px", margin: "0 auto", pb: 10 }}>
      {/* Header Banner Block */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          textAlign: "center",
          width: "100%",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Hello, {user.name ? user.name.split(" ")[0] : "User"}!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome to Lionheart.
          </Typography>
        </Box>
      </Box>

      {/* SECTION 1: ATTENTION REQUIRED ALERTS */}
      <Stack spacing={2} sx={{ mb: 4, alignItems: "center" }}>
        {!user.birthday && (
          <Alert
            severity="warning"
            sx={{
              display: "flex",
              width: "70%",
              alignItems: "center", // Vertically centers text and button perfectly
              "& .MuiAlert-message": { padding: "0" }, // Removes extra baseline text spacing padding
              "& .MuiAlert-action": { padding: "0", alignItems: "center" },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate("/profile")}
              >
                Fix Now
              </Button>
            }
          >
            <strong>Missing Information:</strong> Your user profile does not
            contain a birthday!
          </Alert>
        )}

        {user.planType === "NONE" && (
          <Alert
            severity="info"
            sx={{
              display: "flex",
              width: "70%",
              alignItems: "center", // Vertically centers text and button perfectly
              "& .MuiAlert-message": { padding: "0" }, // Removes extra baseline text spacing padding
              "& .MuiAlert-action": { padding: "0", alignItems: "center" },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate("/insurance")}
              >
                Select Plan
              </Button>
            }
          >
            <strong>Insurance Status:</strong> You have not signed up for an
            insurance plan!
          </Alert>
        )}
      </Stack>

      {/* SECTION 2: CORE EMPLOYEE APPLICATION GRID */}
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
      >
        Employee Portal
      </Typography>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between",
              borderRadius: 2,
            }}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mb: 1, color: "white" }}
              >
                <SecurityIcon />
                <Typography variant="subtitle1" fontWeight="bold">
                  Insurance Portal
                </Typography>
              </Stack>
              <Typography variant="body1">
                Current Plan:{" "}
                {user.planType === "NONE"
                  ? "No plan selected!"
                  : user.planType === "OPT_OUT"
                    ? "Opted out of insurance"
                    : user.planType}{" "}
                {user.vision
                  ? user.dental
                    ? "with Vision and Dental"
                    : "with Vision"
                  : user.dental && "with Dental"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click below to see details or enroll in a different plan.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => navigate("/insurance")}
            >
              View Coverage
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between",
              borderRadius: 2,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mb: 1, color: "white" }}
              >
                <GroupsIcon />
                <Typography variant="subtitle1" fontWeight="bold">
                  Meeting Logger
                </Typography>
              </Stack>

              {/* Conditional Styling: Highlight when > 0, Normal Typography when 0 */}
              {user.pendingMeetings > 0 ? (
                <Box>
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{
                      backgroundColor: "warning.dark",
                      color: "warning.contrastText",
                      px: 2,
                      py: 0,
                      borderRadius: 2,
                      fontWeight: "bold",
                      display: "inline-block",
                      boxShadow: 2,
                    }}
                  >
                    You have {user.pendingMeetings} pending meeting
                    {user.pendingMeetings === 1 ? "" : "s"}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", my: 0 }}
                >
                  You have no pending meetings.
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click to view meeting details or schedule a new meeting.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => navigate("/meetings")}
            >
              Meetings
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between",
              borderRadius: 2,
            }}
          >
            <Box textAlign="center">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mb: 1, color: "white" }}
              >
                <ReceiptLongIcon />
                <Typography variant="subtitle1" fontWeight="bold">
                  Reimbursements
                </Typography>
              </Stack>
              <Typography variant="body1">
                You have {user.pendingReimbursements} pending reimbursements
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click to see more details or file a new reimbursement.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => navigate("/reimbursements")}
            >
              Reimbursements
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between",
              borderRadius: 2,
            }}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mb: 1, color: "white" }}
              >
                <AccessTimeIcon />
                <Typography variant="subtitle1" fontWeight="bold">
                  Time Tracking
                </Typography>
              </Stack>
              <Typography variant="body1">
                {!user.hoursLogged
                  ? "You have not yet logged your hours for last month!"
                  : "Last month you logged " +
                    user.hours +
                    " hours and " +
                    user.overtimeHours +
                    " overtime hours"}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Log hours worked or view previous months hours
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => navigate("/invoice/hours")}
            >
              Log Hours
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* SECTION 3: CONDITIONAL ADMINISTRATIVE SUITE SECTION */}
      {user.admin && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 4 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ mb: 2, color: "secondary.main" }}
          >
            <AdminPanelSettingsIcon />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Admin Tools
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  Candidates Portal
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  View perspective candidate profiles and update thier progress.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/candidates/view")}
                >
                  Manage Candidates
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  Invoicing
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Upload templates to create new invoices.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/invoice/create")}
                >
                  Generate Invoices
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover" }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ mb: 0.5 }}
                >
                  <AssessmentIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Insurance Dashboard
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  View company insurance information.
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/insurance/dashboard")}
                  >
                    Costs Dashboard
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/insurance/rates/dashboard")}
                  >
                    Rates Dashboard
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  System Administration
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Manage employees and modify notification settings.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/admin/notifications")}
                  >
                    Notification Settings
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/admin")}
                  >
                    Employee Management
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default HomePage;
